import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, type SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Producer, MilkControlSummary } from '@homeleite/domain';
import { MilkControlsApiRepository } from '../../../../../../../../libs/domain/src/lib/milk-control/milk-controls-api.repository';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Filter, FilterOp, InterceptorConfig, SortMode, SortOrder } from '@homeleite/arch';
import { catchError, map, of } from 'rxjs';
import { milkControlToImportPackage } from './milk-control-import-json';
import { PdfGeneratorService } from '../../../../../../../homeleite/src/app/features/milk-controls/milking-control-details/pdf-generator.service';
import {
    loadMilkControlDetail,
    MILK_CONTROL_DIALOG_CONFIG, ProducerDetailsMilkControlAnimalsDialogComponent
} from './producer-details-milk-control-animals-dialog.component';

@Component({
    selector: 'homeleite-producer-details-milk-controls',
    templateUrl: './producer-details-milk-controls.component.html',
    styleUrls: ['./producer-details-milk-controls.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProducerDetailsMilkControlsComponent implements OnChanges {
    @Input() producer: Producer;

    public milkControls: MilkControlSummary[] = [];
    public isLoading = false;
    public hasError = false;
    public exportingId: string | null = null;
    public pdfId: string | null = null;

    constructor(
        private repository: MilkControlsApiRepository,
        private changeDetection: ChangeDetectorRef,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private pdf: PdfGeneratorService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['producer'] && this.producer?.id) {
            this.loadProducerMilkControls(this.producer.id);
        }
    }

    private loadProducerMilkControls(producerId: string): void {
        this.isLoading = true;
        this.hasError = false;

        // HML: summariesByProducer. Produção: endpoint pode faltar → listFull + origin via auditData.
        const sorting = [new SortOrder('startedAt', SortMode.DSC)];
        const listFullFallback = () =>
            this.repository
                .listFull(
                    '',
                    [new Filter('producer.$id', FilterOp.EQ, producerId)],
                    sorting,
                    undefined,
                    { autoCatch: InterceptorConfig.NO_INTERCEPT }
                )
                .pipe(
                    map((rows) =>
                        (rows || []).map((r) => MilkControlSummary.fromControlLike(r)).filter(Boolean)
                    ),
                    catchError(() =>
                        this.repository
                            .listFull(
                                '',
                                [new Filter('producerId', FilterOp.EQ, producerId)],
                                sorting,
                                undefined,
                                { autoCatch: InterceptorConfig.NO_INTERCEPT }
                            )
                            .pipe(
                                map((rows) =>
                                    (rows || [])
                                        .map((r) => MilkControlSummary.fromControlLike(r))
                                        .filter(Boolean)
                                ),
                                catchError(() => of(null as MilkControlSummary[] | null))
                            )
                    )
                );

        this.repository
            .listSummariesByProducer(producerId)
            .pipe(
                map((rows) => (rows || []).map((r) => MilkControlSummary.fromControlLike(r)).filter(Boolean)),
                catchError(() => listFullFallback())
            )
            .subscribe({
                next: (milkControlsFromApi) => {
                    if (milkControlsFromApi == null) {
                        this.isLoading = false;
                        this.hasError = true;
                        this.changeDetection.detectChanges();
                        return;
                    }
                    this.milkControls = milkControlsFromApi || [];
                    this.isLoading = false;
                    this.hasError = false;
                    this.changeDetection.detectChanges();
                },
                error: () => {
                    this.isLoading = false;
                    this.hasError = true;
                    this.changeDetection.detectChanges();
                }
            });
    }

    public onOpenAnimals(control: MilkControlSummary): void {
        this.dialog.open(ProducerDetailsMilkControlAnimalsDialogComponent, {
            ...MILK_CONTROL_DIALOG_CONFIG,
            data: { summary: control, producer: this.producer }
        });
    }

    public onPdf(control: MilkControlSummary): void {
        if (!control?.id || this.pdfId) return;
        this.pdfId = control.id;
        this.changeDetection.detectChanges();
        loadMilkControlDetail(this.repository, control, this.producer).subscribe({
            next: async (loaded) => {
                const hasAnimals = loaded?.milkings?.some((milking) => milking.milkedAnimals?.length);
                if (!hasAnimals) {
                    this.pdfId = null;
                    this.snackBar.open('Este controle não tem animais para gerar o PDF.', 'Fechar', { duration: 5000 });
                    this.changeDetection.detectChanges();
                    return;
                }
                try {
                    await this.pdf.generateMilkControlReport(loaded);
                } catch {
                    this.snackBar.open('Não foi possível gerar o PDF do controle.', 'Fechar', { duration: 5000 });
                }
                this.pdfId = null;
                this.changeDetection.detectChanges();
            },
            error: () => {
                this.pdfId = null;
                this.snackBar.open('Não foi possível gerar o PDF do controle.', 'Fechar', { duration: 5000 });
                this.changeDetection.detectChanges();
            }
        });
    }

    public onExportJson(control: MilkControlSummary): void {
        if (!control?.id || this.exportingId) return;
        this.exportingId = control.id;
        this.changeDetection.detectChanges();

        const local = milkControlToImportPackage(control, this.producer?.code);
        const source$ = local.ttScl.length
            ? of(local)
            : this.repository.exportImportJson(control.id).pipe(
                  catchError(() =>
                      this.repository.find(control.id, { autoCatch: InterceptorConfig.NO_INTERCEPT }).pipe(
                          map((loaded) => milkControlToImportPackage(loaded, this.producer?.code))
                      )
                  )
              );

        source$.subscribe({
            next: (payload) => {
                const rows = payload?.ttScl ?? payload?.controls ?? [];
                this.exportingId = null;
                if (!Array.isArray(rows) || rows.length === 0) {
                    this.snackBar.open('Este controle não tem animais lançados para gerar o JSON.', 'Fechar', {
                        duration: 5000,
                    });
                    this.changeDetection.detectChanges();
                    return;
                }
                const date = (control.formattedStartDate || 'controle').replace(/\//g, '-');
                const fileName = `controle-leiteiro-${date}-${control.id.slice(0, 8)}.json`;
                const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.click();
                URL.revokeObjectURL(url);
                this.changeDetection.detectChanges();
            },
            error: () => {
                this.exportingId = null;
                this.snackBar.open('Não foi possível gerar o JSON do controle.', 'Fechar', {
                    duration: 5000,
                });
                this.changeDetection.detectChanges();
            },
        });
    }

    public onDeleteControl(control: MilkControlSummary): void {
        this.repository.delete(control as any).subscribe({
            next: () => {
                this.milkControls = this.milkControls.filter((item) => item.id !== control.id);
                this.changeDetection.detectChanges();
            },
            error: () => {
                this.snackBar.open('Não foi possível excluir o controle leiteiro.', 'Fechar', {
                    duration: 5000,
                });
            },
        });
    }
}
